import type { Map as MapboxMap } from "mapbox-gl";

export interface AssetIconOption {
  value: string;
  label: string;
}

export const ASSET_ICON_OPTIONS: AssetIconOption[] = [
  { value: "wind", label: "Wind Turbine" },
  { value: "sun", label: "Solar" },
  { value: "water", label: "Water Drop" },
  { value: "nuclear", label: "Nuclear" },
];

const ICON_SIZE = 48;

/**
 * Draw a wind turbine icon on a canvas context.
 * @param filled - true for solid fill, false for outline only
 */
const drawWindIcon = (ctx: CanvasRenderingContext2D, size: number, filled: boolean) => {
  const s = size / 32; // scale factor from 32x32 design space
  ctx.save();
  ctx.clearRect(0, 0, size, size);

  if (filled) {
    ctx.fillStyle = "black";
  } else {
    ctx.fillStyle = "transparent";
    ctx.strokeStyle = "black";
    ctx.lineWidth = 1.8 * s;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
  }

  // Hub
  ctx.beginPath();
  ctx.arc(16 * s, 11 * s, 2.5 * s, 0, Math.PI * 2);
  if (filled) ctx.fill(); else ctx.stroke();

  // Blade 1 — top
  ctx.beginPath();
  ctx.moveTo(16 * s, 8.5 * s);
  ctx.lineTo(14.5 * s, 1.5 * s);
  ctx.quadraticCurveTo(16 * s, 0.5 * s, 17.5 * s, 1.5 * s);
  ctx.closePath();
  if (filled) ctx.fill(); else ctx.stroke();

  // Blade 2 — bottom-left
  ctx.beginPath();
  ctx.moveTo(14 * s, 12.5 * s);
  ctx.lineTo(7.5 * s, 16 * s);
  ctx.quadraticCurveTo(7 * s, 14.5 * s, 8.5 * s, 13.5 * s);
  ctx.closePath();
  if (filled) ctx.fill(); else ctx.stroke();

  // Blade 3 — bottom-right
  ctx.beginPath();
  ctx.moveTo(18 * s, 12.5 * s);
  ctx.lineTo(24.5 * s, 16 * s);
  ctx.quadraticCurveTo(25 * s, 14.5 * s, 23.5 * s, 13.5 * s);
  ctx.closePath();
  if (filled) ctx.fill(); else ctx.stroke();

  // Tower
  ctx.beginPath();
  ctx.moveTo(14.8 * s, 13.5 * s);
  ctx.lineTo(14.8 * s, 27 * s);
  ctx.lineTo(17.2 * s, 27 * s);
  ctx.lineTo(17.2 * s, 13.5 * s);
  if (filled) ctx.fill(); else ctx.stroke();

  // Base
  ctx.beginPath();
  ctx.moveTo(11 * s, 27 * s);
  ctx.lineTo(21 * s, 27 * s);
  ctx.lineTo(21 * s, 29 * s);
  ctx.lineTo(11 * s, 29 * s);
  ctx.closePath();
  if (filled) ctx.fill(); else ctx.stroke();

  ctx.restore();
};

/**
 * Draw a sun icon on a canvas context.
 * @param filled - true for solid fill, false for outline only
 */
const drawSunIcon = (ctx: CanvasRenderingContext2D, size: number, filled: boolean) => {
  const s = size / 32; // scale factor
  ctx.save();
  ctx.clearRect(0, 0, size, size);
  ctx.lineCap = "round";

  // Center circle
  ctx.beginPath();
  ctx.arc(16 * s, 16 * s, 6 * s, 0, Math.PI * 2);
  if (filled) {
    ctx.fillStyle = "black";
    ctx.fill();
  } else {
    ctx.strokeStyle = "black";
    ctx.lineWidth = 1.8 * s;
    ctx.stroke();
  }

  // Rays — always drawn as lines
  ctx.strokeStyle = "black";
  ctx.lineWidth = filled ? 2.8 * s : 1.8 * s;

  const rays = [
    [16, 2.5, 16, 7.5],     // top
    [16, 24.5, 16, 29.5],   // bottom
    [2.5, 16, 7.5, 16],     // left
    [24.5, 16, 29.5, 16],   // right
    [6.4, 6.4, 9.8, 9.8],   // top-left
    [22.2, 22.2, 25.6, 25.6], // bottom-right
    [6.4, 25.6, 9.8, 22.2],   // bottom-left
    [22.2, 9.8, 25.6, 6.4],   // top-right
  ];

  rays.forEach(([x1, y1, x2, y2]) => {
    ctx.beginPath();
    ctx.moveTo(x1 * s, y1 * s);
    ctx.lineTo(x2 * s, y2 * s);
    ctx.stroke();
  });

  ctx.restore();
};

/**
 * Draw a water drop icon on a canvas context.
 * Fill version: solid teardrop shape (interior changes color).
 * Stroke version: outline of the drop + small inner highlight curve.
 */
const drawWaterIcon = (ctx: CanvasRenderingContext2D, size: number, filled: boolean) => {
  const s = size / 32;
  ctx.save();
  ctx.clearRect(0, 0, size, size);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  // Teardrop path
  ctx.beginPath();
  ctx.moveTo(16 * s, 2 * s);
  ctx.bezierCurveTo(16 * s, 2 * s, 6 * s, 16 * s, 6 * s, 21 * s);
  ctx.bezierCurveTo(6 * s, 26.5 * s, 10.5 * s, 30 * s, 16 * s, 30 * s);
  ctx.bezierCurveTo(21.5 * s, 30 * s, 26 * s, 26.5 * s, 26 * s, 21 * s);
  ctx.bezierCurveTo(26 * s, 16 * s, 16 * s, 2 * s, 16 * s, 2 * s);
  ctx.closePath();

  if (filled) {
    ctx.fillStyle = "black";
    ctx.fill();
  } else {
    ctx.strokeStyle = "black";
    ctx.lineWidth = 2.2 * s;
    ctx.stroke();

    // Inner highlight curve — follows the bottom-left curvature of the drop
    ctx.beginPath();
    ctx.moveTo(10 * s, 25 * s);
    ctx.bezierCurveTo(11.5 * s, 27 * s, 14 * s, 28 * s, 16 * s, 27.5 * s);
    ctx.lineWidth = 1.8 * s;
    ctx.stroke();
  }

  ctx.restore();
};

/**
 * Draw a nuclear / radiation symbol on a canvas context.
 * Fill version: solid background circle (the part that changes color).
 * Stroke version: the trefoil blades + center circle + outer ring (stays dark).
 */
const drawNuclearIcon = (ctx: CanvasRenderingContext2D, size: number, filled: boolean) => {
  const s = size / 32;
  const cx = 16 * s;
  const cy = 16 * s;
  ctx.save();
  ctx.clearRect(0, 0, size, size);

  if (filled) {
    // Fill: solid circle background — this is what gets colored
    ctx.fillStyle = "black";
    ctx.beginPath();
    ctx.arc(cx, cy, 14 * s, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // Stroke: draw the trefoil radiation symbol

    // Outer ring
    ctx.strokeStyle = "black";
    ctx.lineWidth = 2 * s;
    ctx.beginPath();
    ctx.arc(cx, cy, 14 * s, 0, Math.PI * 2);
    ctx.stroke();

    // Three trefoil blades
    ctx.fillStyle = "black";
    const innerR = 4.5 * s;
    const outerR = 12 * s;
    const gapAngle = Math.PI / 9; // gap between blades

    for (let i = 0; i < 3; i++) {
      const baseAngle = (i * 2 * Math.PI) / 3 - Math.PI / 2;
      const startAngle = baseAngle + gapAngle;
      const endAngle = baseAngle + (2 * Math.PI) / 3 - gapAngle;

      ctx.beginPath();
      ctx.arc(cx, cy, outerR, startAngle, endAngle);
      ctx.arc(cx, cy, innerR, endAngle, startAngle, true);
      ctx.closePath();
      ctx.fill();
    }

    // Center circle (hollow)
    ctx.beginPath();
    ctx.arc(cx, cy, 3 * s, 0, Math.PI * 2);
    ctx.fillStyle = "black";
    ctx.fill();
  }

  ctx.restore();
};

// Registry of draw functions per icon key
const ICON_DRAW_REGISTRY: Record<string, (ctx: CanvasRenderingContext2D, size: number, filled: boolean) => void> = {
  wind: drawWindIcon,
  sun: drawSunIcon,
  water: drawWaterIcon,
  nuclear: drawNuclearIcon,
};

/**
 * Draws the stroke version of an icon onto a canvas element for UI previews.
 * Returns false if the icon key is not found.
 */
export const renderIconPreview = (canvas: HTMLCanvasElement, iconKey: string, size: number = 24): boolean => {
  const drawFn = ICON_DRAW_REGISTRY[iconKey];
  if (!drawFn) return false;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  drawFn(ctx, size, false);
  return true;
};

/**
 * Returns a Mapbox style expression that maps the "icon" feature property
 * to the correct registered image name with the given suffix.
 * e.g. getIconImageExpression("fill") => ["match", ["get", "icon"], "wind", "wind-fill", "sun", "sun-fill", ...]
 */
export const getIconImageExpression = (suffix: string): (string | string[])[] => {
  const pairs = ASSET_ICON_OPTIONS.flatMap(o => [o.value, `${o.value}-${suffix}`]);
  return ["match", ["get", "icon"], ...pairs, ""];
};

/**
 * Returns true if any asset icon images are loaded in the given map instance.
 */
export const hasLoadedAssetIcons = (map: MapboxMap): boolean => {
  return ASSET_ICON_OPTIONS.some(o => map?.hasImage?.(`${o.value}-fill`));
};

/**
 * Creates an ImageData object by drawing an icon on an offscreen canvas.
 */
const createIconImageData = (
  drawFn: (ctx: CanvasRenderingContext2D, size: number, filled: boolean) => void,
  size: number,
  filled: boolean
): ImageData => {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas 2d context");
  drawFn(ctx, size, filled);
  return ctx.getImageData(0, 0, size, size);
};

/**
 * Loads all asset icons into a Mapbox map as SDF images.
 * Call once after the map loads.
 *
 * Registers fill and stroke variants for each icon (e.g. "wind-fill", "wind-stroke")
 * with { sdf: true } so icon-color can tint them at runtime.
 */
export const loadAssetIcons = (map: MapboxMap): void => {
  for (const [key, drawFn] of Object.entries(ICON_DRAW_REGISTRY)) {
    const fillId = `${key}-fill`;
    const strokeId = `${key}-stroke`;

    try {
      if (!map.hasImage(fillId)) {
        const fillData = createIconImageData(drawFn, ICON_SIZE, true);
        map.addImage(fillId, fillData, { sdf: true });
      }

      if (!map.hasImage(strokeId)) {
        const strokeData = createIconImageData(drawFn, ICON_SIZE, false);
        map.addImage(strokeId, strokeData, { sdf: true });
      }
    } catch (err) {
      console.error(`[AssetIcons] Failed to load asset icon "${key}":`, err);
    }
  }
};
